import type { RouteParams } from "../../routes/$types";

import { queryPage, querySpecialPage } from '$lib/api/queries';
import { gqlClient, injectTranslations } from '$lib/util/directus';
import { error } from '@sveltejs/kit';
import { page } from "$app/stores";
import { get } from "svelte/store";

type PageBuilderCollection = 'pages' | 'special-pages' | 'posts' | 'product';

export type SectionConfig = {
	/** the query to load the section */
	query: string;

	/** the name of the collection in directus */
	collection: string;

	/** (optional) boolean indicating that the section should not be attached to the queries */
	disabled?: boolean;

	/** used for loading inital data on the server side */
	load?: (data: any, params: RouteParams, additionalLoadData?: any) => Promise<Record<string, any>>;

	/** select for which pages builders tis section should be available */
	availableFor?: PageBuilderCollection[];
};

export type MenuConfig = {
	query: string;
	collection: string;
};

const modules = import.meta.glob<Record<string, any>>('$lib/sections/**/*.svelte', { eager: true });

/** get map of all section where the key represents the name and the value a reference to the relevant component */
function getAllSections() {
	const sections: Record<string, any> = {};

	for (const path of Object.keys(modules)) {
		const module = modules[path]

		if('config' in module === false) continue

		const config = module.config as SectionConfig;

		if (config.disabled) continue;

		sections[config.collection] = module.default;
	}

	return sections;
}

/** get map of all section where the key represents the name and the value a reference to the relevant component */
function getAllSectionConfigs() {
	const sections: Record<string, SectionConfig> = {};

	for (const path of Object.keys(modules)) {
		const module = modules[path];
		const config = module.config as SectionConfig;

		if (config.disabled) continue;

		sections[config.collection] = config;
	}

	return sections;
}

/** map of all section where the key represents the name and the value a reference to the relevant component */
export const ALL_SECTIONS = getAllSections();
export const ALL_SECTION_CONFIGS = getAllSectionConfigs();

/**
 * Get the subquery for all avaiable sections to use in an gql query
 *
 * @param sectionNames a list of names that are used as filter
 * @param exclude if the items from sectionNames should be excluded or included
 * @returns a subquery for the relevant sections
 */
export function getSectionsQuery(collection: PageBuilderCollection) {
	const queryParts = [];

	for (const path of Object.keys(modules)) {
		const module = modules[path];
		
		const config = module.config as SectionConfig;

		if (config.disabled) continue;
		if (!config.availableFor?.includes(collection)) continue;

		queryParts.push(
			'... on ' +
			config.collection +
			' {\n  ' +
			(config.query?.trim().replaceAll('    ', '  ') ?? '') +
			'\n}'
		);
	}

	return queryParts.join('\n');
}

/**
 * Loads the page data with all section, calls the load function of all the sections.
 * (It's basically the code that was in the page.server.ts load function so that it can be easily reused)
 * @param params The params object from the svelte load function
 * @param previewHeader The preview header from the previewStore
 */
export async function getPage(params: any, additionalLoadData: any, previewHeader: HeadersInit) {
	const slug = params.path === '' ? 'home' : params.path;

	const res = await injectTranslations(
		await gqlClient.request(queryPage(slug, params.lang), {}, previewHeader),
		params.lang
	);

	const page = res.pages[0];

	if (!page) {
		error(404, 'Not found');
	}

	const additionalQueries = [];

	for (const section of page.sections || []) {
		const config = ALL_SECTION_CONFIGS[section.collection];

		if (!config || !config.load) continue;

		additionalQueries.push(async () => {
			if (!config.load) return;

			const additionalData = await config.load(section.item, params, additionalLoadData);
			section.additionalData = additionalData;
		});
	}

	// Load additional data for every section
	await Promise.all(additionalQueries.map((fn) => fn()));

	return page
}

/**
 * Loads special page and calls the load function of the sections
 * @param name The name of the special page
 * @param previewHeader The preview header from the previewStore
 * @param additionalLoadData Optional data that will be passed to the section load function
 * @returns The global special page sections
 */
export async function getSpecialPage(name: string, params: any, additionalLoadData: any, previewHeader: HeadersInit) {
	const res = await injectTranslations(
		await gqlClient.request(querySpecialPage(name), {}, previewHeader),
		params.lang
	);

	const page = res.special_pages[0];

	const additionalQueries = [];

	for (const section of page.sections || []) {
		const config = ALL_SECTION_CONFIGS[section.collection];

		if (!config || !config.load) continue;

		additionalQueries.push(async () => {
			if (!config.load) return;
			const additionalData = await config.load(section.item, params, additionalLoadData);
			section.additionalData = additionalData;
		});
	}

	// Load additional data for every section
	await Promise.all(additionalQueries.map((fn) => fn()));

	return page
}

/**
 * Returns the slug of a special page in the correct language
 * @param specialPagesData The special page data from $page.data.specialPagesData
 * @param currentLang The current language
 * @param specialPageName The name of the special page
 * @returns The slug
 */
export function getCurrentPrefixSlug(specialPagesData: any, currentLang: string, specialPageName: string) {
	for (const specialPage of specialPagesData) {
		if (specialPage.name === specialPageName) {
			for (const lang of specialPage.translations) {
				if (lang.languages_code === currentLang) {
					return lang.slug;
				}
			}
		}
	}
	return '';
}


export function getPreviewHeader(previewToken?: string | null) {
	if(!previewToken && typeof window === 'undefined') return {} as HeadersInit;

	const previewtoken =  previewToken ?? get(page)?.data?.previewToken

	if (!previewtoken) return {} as HeadersInit;

	return {
		Authorization: `Bearer ${previewtoken}`
	};
}